<template>
  <div v-show="!hidden" :id="baseFormId" @submit.stop.prevent="onSubmit($event)">
    <slot name="header"></slot>
    <form-lock-store v-if="!isReadView" />
    <div>
      <el-tabs v-if="arePagesMultiple" type="card" v-model="activePageId" @tab-click="handleTabClick">
        <el-tab-pane v-for="{ id, label } in visiblePages" :name="id" :key="id">
          <template :slot="'label'">
            <span :style="{ opacity: activePageId === id ? 1 : 0.4 }">{{ $i18n.t(label) }}</span>
          </template>
          <form-page-store :page-id="id" :path="pagePath(id)" :form-read-only="formReadOnly" />
          <transition-group mode="out-in" name="slide-fade" appear></transition-group>
        </el-tab-pane>
      </el-tabs>
      <div class="panel-group" v-else>
        <div v-for="{ id } in visiblePages" :key="id">
          <form-page-store :page-id="id" :path="pagePath(id)" :form-read-only="formReadOnly" />
        </div>
      </div>
    </div>

    <slot name="buttons"> </slot>
  </div>
</template>

<script>
import FormLockStore from './FormLockStore'
import FormGroupStore from './FormFieldStore.vue'

import SubFormStore from './SubFormStore'
import LeafletControlStore from './store-controls/LeafletControlStore'
import DocumentPropStore from './store-controls/DocumentPropStore'
import FormSectionStore from './store-controls/FormSectionStore'

import Collapse from '../../components/Collapse'
import { concatPath } from '../../utils'
import FormPageStore from './FormPageStore'
import { get } from 'lodash'

/**
 * @class BaseFormStore
 *
 * @description This component is the state handler/wrapper for BaseForm
 */
export default {
  name: 'base-form-store',
  components: {
    FormGroupStore,
    SubFormStore,
    LeafletControlStore,
    DocumentPropStore,
    Collapse,
    FormLockStore,
    FormSectionStore,
    FormPageStore,
  },
  data() {
    return {
      closedGroups: [],
      showTransition: true,
      bTabKey: 0,
      initialExpressionTypes: {
        readOnlyExp: 'readOnly',
        hiddenExp: 'hidden',
      },
      accordionNumber: 0,
      loadNumber: 0,
    }
  },
  computed: {
    baseFormId() {
      return `${this.$storeNamespace}-${this.form.id}`
    },
    isReadView() {
      return this.$get('isReadView')
    },
    idMainTable() {
      return this.$get('idMainTable')
    },
    showHistory() {
      return this.$get('showHistory')
    },
    subformView() {
      return this.$get('subformView')
    },
    submitRoot() {
      return this.$get('submitRoot')
    },
    groupAutoCollapse() {
      return this.$get('groupAutoCollapse')
    },
    collapsedDefaultClose() {
      return this.$get('collapsedDefaultClose')
    },
    usernameHidden() {
      return this.$get('usernameHidden')
    },
    form() {
      return this.$get('form')
    },
    readOnlyExp() {
      return this.form.readOnlyExp
    },
    hiddenExp() {
      return this.form.hiddenExp
    },
    lock() {
      return this.$get('formLock')
    },
    hidden: {
      get() {
        return this.form.hidden || false
      },
      set(newValue) {
        this.$commit('FORM_UPDATE_PROP', { key: 'hidden', value: newValue })
      },
    },
    collapsed: {
      get() {
        return 0
      },
      set(value) {
        this.$commit('FORM_UPDATE_GROUP_VALUE', {
          path: concatPath([value.pid, value.gid]),
          key: 'collapsed',
          value: value.value,
        })
      },
    },
    activePageId: {
      get() {
        return this.$get('activePageId')
      },
      set(activePageId) {
        this.$dispatch('setActivePageId', { activePageId })
      },
    },
    formReadOnly: {
      get() {
        return !!(this.form.readOnly || this.lock.isLocked || this.isReadView)
      },
      set(newValue) {
        this.$commit('FORM_UPDATE_READONLY', { readOnly: !!newValue })
      },
    },
    arePagesMultiple() {
      return this.visiblePages.length > 1
    },
    visiblePages() {
      return this.$get('visiblePages')
    },
  },
  methods: {
    handleTabClick(tab) {
      this.activePageId = tab.name
    },
    onSubmit($event) {
      return this.$listeners.submit($event)
    },
    pagePath(pageId) {
      return String(get(this.form, 'pages', []).findIndex(({ id }) => id === pageId))
    },
  },
}
</script>

<style lang="scss" scoped>
.nav-link.active {
  background-color: $brandColor !important;
  border-radius: 0.286em;
  color: white !important;
}
.fieldset {
  margin: 0;
  padding: 0;
  .fieldset-legend {
    font-size: inherit;
    margin-bottom: 0;
  }
}
.panel-group {
  width: 100%;
  margin-top: 15px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
}
.disabled {
  cursor: not-allowed;
  pointer-events: none;
  .panel-heading {
    pointer-events: all;
  }
  .card-header {
    pointer-events: all;
  }
  .form-control {
    background-color: rgb(238, 238, 238);
  }
}
.panel-refresh {
  padding: 0 10px 0 10px;
  color: $brandColor;
  &:before {
    content: none !important;
  }
  &:hover {
    cursor: pointer;
  }
}
.card {
  width: 100%;
  &.single-page {
    border: none;
    display: block;
    .card-header {
      display: none;
    }
    .card-body {
      padding: 0;
    }
  }
}
.rotate {
  animation: rotating 2s linear;
  animation-iteration-count: infinite;
}
@keyframes rotating {
  from {
    -ms-transform: rotate(0deg);
    -moz-transform: rotate(0deg);
    -webkit-transform: rotate(0deg);
    -o-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  to {
    -ms-transform: rotate(360deg);
    -moz-transform: rotate(360deg);
    -webkit-transform: rotate(360deg);
    -o-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
.opacity-pulse {
  animation: opacityPulse 2s ease-out;
  animation-iteration-count: infinite;
  opacity: 1;
}
.form-utility {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.sticky {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 3;
}
/*
  .opacity-pulse:hover {
    animation: none;
  }
  */
.tab-pane.card-body.fade {
  visibility: hidden;
  opacity: 0;
  transition:
    visibility 0s 2s,
    opacity 2s linear;
  &.active {
    visibility: visible;
    opacity: 1;
    transition: opacity 2s linear;
  }
  // opacity: 1;
  // transition : all .3s;
  // -webkit-transition : all .3s;
  // -moz-transition : all .3s;
}
@keyframes opacityPulse {
  0% {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}
.nav.nav-tabs.card-header-tabs {
  &:focus {
    outline: none;
  }
}
.nav > li > a[disabled] {
  display: none !important;
}
.nav-link.hiddenTabLink {
  display: none;
}
</style>
